// src/components/admin/SupportTicketModal.jsx - Client ticket creation modal
import React, { useState, useEffect } from 'react';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { createTicket, getClientEmailBySalonId } from '../../api/ticketService';

const SEVERITY_OPTIONS = [
  '1 - System Down',
  '2 - Significant Impact',
  '3 - Minor Impact',
  '4 - Informational Only'
];

const TYPE_OPTIONS = [
  'Technical Issues',
  'Billing and Payments',
  'Product Inquiries',
  'Feedback',
  'Others'
];

function SupportTicketModal({ salonId, onClose }) {
  const { currentUser } = useSalonAuth();
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    ticketHeader: '',
    ticketDescription: '',
    ticketSeverity: '3 - Minor Impact',
    ticketType: 'Technical Issues'
  });

  useEffect(() => {
    const fetchEmail = async () => {
      const email = await getClientEmailBySalonId(salonId);
      setClientEmail(email);
    };
    fetchEmail();
  }, [salonId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ticketHeader.trim() || !formData.ticketDescription.trim()) return;

    try {
      setLoading(true);
      await createTicket({
        salonId,
        clientEmail,
        senderName: currentUser?.name || currentUser?.email || 'Client',
        ...formData
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ticket-modal-overlay" onClick={onClose}>
      <div className="ticket-modal-content" onClick={e => e.stopPropagation()}>
        <div className="ticket-modal-header">
          <h2>Submit a Support Ticket</h2>
          <button className="ticket-modal-close" onClick={onClose}>&times;</button>
        </div>

        {submitted ? (
          <div className="ticket-confirmation">
            <h3>Ticket Submitted Successfully</h3>
            <p>Your support ticket has been created and our team has been notified.</p>
            <p>We aim to respond to all tickets within 24 hours during business days.</p>
            <p>You can track the status of your ticket by clicking "View my tickets" on the modules page.</p>
            <button className="ticket-btn-submit" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="ticket-modal-body">
              <div className="ticket-form-group">
                <label>Salon ID</label>
                <input type="text" value={salonId} readOnly />
              </div>

              <div className="ticket-form-group">
                <label>Client Email</label>
                <input type="text" value={clientEmail || 'Loading...'} readOnly />
              </div>

              <div className="ticket-form-group">
                <label>Ticket Header *</label>
                <input
                  type="text"
                  name="ticketHeader"
                  value={formData.ticketHeader}
                  onChange={handleChange}
                  placeholder="Brief summary of your issue"
                  required
                />
              </div>

              <div className="ticket-form-group">
                <label>Description *</label>
                <textarea
                  name="ticketDescription"
                  value={formData.ticketDescription}
                  onChange={handleChange}
                  placeholder="Describe your issue in detail..."
                  required
                />
              </div>

              <div className="ticket-form-group">
                <label>Severity</label>
                <select
                  name="ticketSeverity"
                  value={formData.ticketSeverity}
                  onChange={handleChange}
                >
                  {SEVERITY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="ticket-form-group">
                <label>Type</label>
                <select
                  name="ticketType"
                  value={formData.ticketType}
                  onChange={handleChange}
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ticket-modal-actions">
              <button type="button" className="ticket-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="ticket-btn-submit" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SupportTicketModal;
